import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Box, Button, IconButton, InputAdornment, Link, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Visibility, VisibilityOff } from '@material-ui/icons';

import { useLogin } from '../../hooks/use-login.hook';
import { LoginRequestDTO } from '../../types/authentication.dto';
import { PUBLIC_PANELS } from '../../../shared/public/constants/panel.constants';
import { clientAccentOverrides } from './authentication-form.styles';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface LoginFormProps {
  onSuccess: () => void;
  onNavigate: (panel: string) => void;
}

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    ...clientAccentOverrides(theme),
  },
  submit: {
    backgroundColor: theme.palette.client.primary.main,
    color: '#fff',
    minHeight: 48,
  },
  toggle: { minWidth: 44, minHeight: 44 },
  links: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(5),
    marginTop: theme.spacing(1),
  },
}));

const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onNavigate }) => {
  const classes = useStyles();
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const { isSubmitting, error, submit } = useLogin();

  const { control, handleSubmit, formState } = useForm<LoginRequestDTO>({
    mode: 'onSubmit',
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    const user = await submit(values);

    if (user) onSuccess();
  });

  return (
    <form className={classes.form} onSubmit={onSubmit} noValidate>
      <Controller
        name="email"
        control={control}
        rules={{
          required: 'Veuillez saisir votre adresse mail',
          pattern: { value: EMAIL_PATTERN, message: "L'adresse mail n'est pas valide" },
        }}
        render={({ field }) => (
          <TextField
            {...field}
            inputRef={field.ref}
            autoFocus
            label="Adresse mail"
            type="email"
            autoComplete="email"
            variant="outlined"
            error={Boolean(formState.errors.email)}
            helperText={formState.errors.email?.message}
            inputProps={{ name: 'email', 'aria-invalid': Boolean(formState.errors.email) }}
          />
        )}
      />

      <Controller
        name="password"
        control={control}
        rules={{ required: 'Veuillez saisir votre mot de passe' }}
        render={({ field }) => (
          <TextField
            {...field}
            inputRef={field.ref}
            label="Mot de passe"
            type={isPasswordVisible ? 'text' : 'password'}
            autoComplete="current-password"
            variant="outlined"
            error={Boolean(formState.errors.password)}
            helperText={formState.errors.password?.message}
            inputProps={{ name: 'password', 'aria-invalid': Boolean(formState.errors.password) }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    className={classes.toggle}
                    aria-label={isPasswordVisible ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    onClick={() => setIsPasswordVisible(!isPasswordVisible)}
                  >
                    {isPasswordVisible ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      />

      <Box aria-live="polite">
        {error && (
          <Typography color="error" variant="body2">
            {error}
          </Typography>
        )}
      </Box>

      <Button className={classes.submit} type="submit" variant="contained" disabled={isSubmitting}>
        Se connecter
      </Button>

      <div className={classes.links}>
        <Link component="button" type="button" onClick={() => onNavigate(PUBLIC_PANELS.FORGOT_PASSWORD)}>
          Mot de passe oublié
        </Link>
        <Link component="button" type="button" onClick={() => onNavigate(PUBLIC_PANELS.SIGNUP)}>
          Créer un compte
        </Link>
      </div>
    </form>
  );
};

export default LoginForm;
